import type { PlanV2IssueCardPayload } from './plan-v2-issues';

/**
 * What a page's review sends, and what it puts aside.
 *
 * The review surface is approve-by-default: every finding on the page arrives
 * ticked, the operator unticks the ones that are wrong or not now, and one
 * keystroke sends the rest. That inverts the old model — approve each item,
 * one at a time — and it is the right way round, because on a healthy queue the
 * exceptions are rarer than the approvals.
 *
 * **The state is the exceptions, not the approvals.** A set of unticked issue
 * keys, rather than a set of ticked ones. Three things fall out of that and all
 * three matter:
 *
 * - A card that arrives late — the technical payload changes when the domain
 *   rollup lands, the links card appears when signals load — is ticked without
 *   anyone doing anything. Tracking approvals would leave it silently unticked
 *   and the operator would send a page missing the finding they were looking at.
 * - An empty set is the common case, so the default costs nothing to represent.
 * - Nothing has to be cleared between pages for correctness. Keys are unique per
 *   URL, so a stale exception cannot leak onto another page's card.
 */

export interface ReviewSelectableItem {
  issueKey: string;
  payload: PlanV2IssueCardPayload;
}

export interface ReviewSelectionSplit {
  /** Ticked — becomes a work item. */
  send: ReviewSelectableItem[];
  /** Unticked — dismissed for this period, and back next period if it recurs. */
  putAside: ReviewSelectableItem[];
}

export function splitReviewSelection(
  items: readonly ReviewSelectableItem[] | null | undefined,
  deselected: ReadonlySet<string>
): ReviewSelectionSplit {
  const send: ReviewSelectableItem[] = [];
  const putAside: ReviewSelectableItem[] = [];
  for (const item of items ?? []) {
    if (!item?.issueKey) continue;
    (deselected.has(item.issueKey) ? putAside : send).push(item);
  }
  return { send, putAside };
}

/**
 * The button's words.
 *
 * It names the count it will send, not the total on the page, because that is
 * the number the operator is agreeing to. When everything has been unticked the
 * verb changes rather than the number going to zero: "Send 0 items" reads as a
 * broken button, and what the press actually does then — put the whole page
 * aside — is a different action and deserves different words.
 */
export function reviewSendLabel(sendCount: number, putAsideCount: number): string {
  // Nothing registered at all — a page whose cards have not mounted yet, or one
  // with no findings. The button is not rendered in that state, but the label
  // must not read "Put 0 items aside" if anything ever asks for it.
  if (sendCount === 0 && putAsideCount === 0) return 'Nothing to send';
  if (sendCount === 0) {
    return putAsideCount === 1 ? 'Put 1 item aside →' : `Put ${putAsideCount} items aside →`;
  }
  return sendCount === 1 ? 'Send 1 item →' : `Send ${sendCount} items →`;
}

/**
 * What the batch endpoint is asked to write.
 *
 * Both halves are sent, and the unticked half is a real write rather than a
 * no-op: "not this one" is a decision, and a decision that leaves no record
 * would ask the same question again on the next visit. It is `dismissed`, which
 * is period-scoped and archived at the chapter turn — so an unticked finding is
 * gone for this period and comes back next period if the crawl still finds it.
 */
export function reviewSendRequest(split: ReviewSelectionSplit): Array<{
  issueKey: string;
  queue: 'work_item' | 'dismissed';
  payload: PlanV2IssueCardPayload;
}> {
  return [
    ...split.send.map((i) => ({ issueKey: i.issueKey, queue: 'work_item' as const, payload: i.payload })),
    ...split.putAside.map((i) => ({ issueKey: i.issueKey, queue: 'dismissed' as const, payload: i.payload }))
  ];
}

/** Undoing a send returns every key it touched to the queue it came from. */
export function reviewUndoRequest(
  sent: readonly { issueKey: string }[]
): Array<{ issueKey: string; queue: 'issues' }> {
  return sent.map((i) => ({ issueKey: i.issueKey, queue: 'issues' as const }));
}
